




























































































































































































































import type { AppliedLesson } from '@/models/appliedlesson';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import MoreInfoModal from '@/components/MoreInfoModal.vue';
import { toggleModal } from '@/helpers/modal';
import type { Project } from '@/models/project';
import FormProjectUserPicker from '@/components/form/FormProjectUserPicker.vue';
import FormDynamicFieldPicker from '@/components/form/FormDynamicFieldPicker.vue';
import type { CompanySetting } from '@/models/companySetting';
import type { User } from '@/models/user';
import type { DynamicField } from '@/models/dynamicField';
import type { AppliedLessonsFilter } from '@/helpers/AppliedLessonsFilter';
import { hasAnyPermission, hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';
import { resolveLessonTypeByLesson } from '@/helpers/resolveLessonType';
import { AppliedLessonLike } from '@/helpers/filter/appliedLessons/filterAppliedLessonTypes';

@Component({
    name: 'AppliedLessonContainer',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasPermission },
    components: {
        MoreInfoModal,
        FormProjectUserPicker,
        FormDynamicFieldPicker,
    },
})
export default class AppliedLessonContainer extends Vue {
    @Prop() private appliedlesson: AppliedLesson;
    @Prop() private tableColumns: AppliedLessonsFilter;
    @Prop() private isUpdating: boolean;
    @Prop({ required: false }) private highlight: number | null;

    @Getter('account/userInfo') private userInfo: User;
    private toggleModal = toggleModal;

    @Getter('project/selectedProject') private selectedProject: Project;
    @Getter('account/companySetting') private companySetting: CompanySetting;
    @Getter('company/dynamicFields') private dynamicFields: DynamicField[];
    @Action('company/fetchAllDynamicFields') private fetchAllDynamicFields: any;

    private MoreInfoModal = MoreInfoModal;

    @Watch('appliedlesson.probability')
    onProbabilityChange(newVal: number) {
        if (newVal < 0) {
            this.appliedlesson.probability = 0;
        } else if (newVal > 100) {
            this.appliedlesson.probability = 100;
        }
    }

    update() {
        // neutralize datepicker timezone
        if (this.appliedlesson.contacttill) {
            this.appliedlesson.contacttill.setMinutes(
                this.appliedlesson.contacttill.getMinutes() - this.appliedlesson.contacttill.getTimezoneOffset()
            );
        }
        this.$emit('update', this.appliedlesson);
    }

    dateFormatter(date) {
        return new Intl.DateTimeFormat('DE-ch', { timeZone: 'UTC' }).format(date);
    }

    mounted() {
        if (!this.dynamicFields || this.dynamicFields.length === 0) {
            this.fetchAllDynamicFields();
        }
        this.highlightLesson();
    }

    highlightLesson() {
        this.$nextTick(() => {
            if (this.highlight) {
                this.$router.replace({
                    path: this.$route.path,
                    query: {
                        ...this.$route.query,
                        highlight: undefined,
                    },
                });
                const target = this.$refs[this.highlight];
                if (target instanceof Element) {
                    target.scrollIntoView({
                        behavior: 'auto',
                        block: 'center',
                    });
                }
            }
        });
    }

    canEditLesson(appliedLesson: AppliedLessonLike) {
        const hasOwnEditPermission = hasPermission(
            resolveLessonTypeByLesson(appliedLesson.lesson, {
                x: PERMISSIONS.APPEX_X_APPLIED_LESSON_EDIT,
                pm: PERMISSIONS.APPEX_PM_APPLIED_LESSON_EDIT,
                trade: PERMISSIONS.APPEX_APPLIED_LESSON_EDIT,
            })
        );

        const hasGlobalEditPermission = hasPermission(
            resolveLessonTypeByLesson(appliedLesson.lesson, {
                x: PERMISSIONS.APPEX_X_APPLIED_LESSON_EDIT_ALL,
                pm: PERMISSIONS.APPEX_PM_APPLIED_LESSON_EDIT_ALL,
                trade: PERMISSIONS.APPEX_APPLIED_LESSON_EDIT_ALL,
            })
        );

        return (
            (hasOwnEditPermission &&
                appliedLesson.lesson.user_id === this.userInfo.id &&
                (!appliedLesson.lesson.approved || appliedLesson.lesson.project_only)) ||
            (hasGlobalEditPermission && appliedLesson.lesson.project_only)
        );
    }

    canEditProperties(appliedLesson: AppliedLessonLike) {
        return hasAnyPermission([
            resolveLessonTypeByLesson(appliedLesson.lesson, {
                x: PERMISSIONS.APPEX_X_APPLIED_LESSON_EDIT,
                pm: PERMISSIONS.APPEX_PM_APPLIED_LESSON_EDIT,
                trade: PERMISSIONS.APPEX_APPLIED_LESSON_EDIT,
            }),
        ]);
    }

    get impact() {
        const maxDynamicFieldValue = this.dynamicFields
            .map((field) => field.value)
            .filter((v): v is number => !!v)
            .reduce((a, b) => Math.max(a, b), 0);
        if (
            this.appliedlesson.dynamic_field &&
            this.appliedlesson.dynamic_field.value &&
            this.appliedlesson.cost &&
            this.appliedlesson.probability &&
            maxDynamicFieldValue
        ) {
            const value = this.appliedlesson.dynamic_field.value / maxDynamicFieldValue;

            const impact = value * this.appliedlesson.cost * (this.appliedlesson.probability / 100);
            return parseFloat(impact.toFixed(2));
        }
        return 0;
    }
}
